import { useState } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

type ListingStatus = 'pending' | 'active' | 'deprecated'

interface Listing {
  id: string
  slug: string
  display_name: string | null
  description: string | null
  server_type: string | null
  status: ListingStatus
  featured: boolean
  category: string | null
  version: string | null
  install_count: number | null
  created_at: string | null
  updated_at: string | null
}
interface ListingsResponse {
  listings: Listing[]
  limit: number
  offset: number
  has_more: boolean
}

interface ListingPatch {
  status?: ListingStatus
  featured?: boolean
  category?: string | null
}

// The backend's legal transitions (anything else is a 409 invalid_transition).
const TRANSITIONS: Record<ListingStatus, { to: ListingStatus; label: string }[]> = {
  pending: [
    { to: 'active', label: 'Approve' },
    { to: 'deprecated', label: 'Reject' },
  ],
  active: [{ to: 'deprecated', label: 'Deprecate' }],
  deprecated: [{ to: 'active', label: 'Reinstate' }],
}

const PAGE_SIZE = 50

function name(l: Listing): string {
  return l.display_name || l.slug
}

function describe(l: Listing, patch: ListingPatch): string {
  const parts: string[] = []
  if (patch.status) parts.push(`status ${l.status} → ${patch.status}`)
  if (patch.featured !== undefined) parts.push(patch.featured ? 'mark featured' : 'remove featured')
  if (patch.category !== undefined)
    parts.push(patch.category === null ? 'clear category' : `category → "${patch.category}"`)
  return parts.join(', ')
}

export function MarketplaceView() {
  const qc = useQueryClient()
  const [status, setStatus] = useState<'' | ListingStatus>('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [categoryDraft, setCategoryDraft] = useState<Record<string, string>>({})
  const [pending, setPending] = useState<{ listing: Listing; patch: ListingPatch } | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (status) params.set('status', status)
  if (search) params.set('search', search)

  const query = useQuery<ListingsResponse>({
    queryKey: ['admin-marketplace', status, search, offset],
    queryFn: () => apiFetch<ListingsResponse>(`/admin/marketplace?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const update = useMutation({
    mutationFn: (vars: { listing: Listing; patch: ListingPatch }) =>
      apiFetch<{ changed: boolean }>(`/admin/marketplace/${encodeURIComponent(vars.listing.slug)}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: (data, vars) => {
      setOkMsg(
        data?.changed === false
          ? `${name(vars.listing)}: nothing changed.`
          : `${name(vars.listing)} updated.`,
      )
      qc.invalidateQueries({ queryKey: ['admin-marketplace'] })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(update.error)

  const listings = query.data?.listings ?? []
  const hasMore = query.data?.has_more ?? false

  return (
    <section className="scr" aria-labelledby="mkt-heading">
      <div className="screen-head">
        <h2 id="mkt-heading">Marketplace</h2>
      </div>

      <div className="scr-filters">
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | ListingStatus)
            setOffset(0)
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">pending</option>
          <option value="active">active</option>
          <option value="deprecated">deprecated</option>
        </select>
        <input
          type="search"
          aria-label="Search listings"
          placeholder="Search name or slug…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOffset(0)
          }}
        />
      </div>

      <p className="scr-note">
        Curation only: approve, reject, deprecate or reinstate, plus featured and category.
        Listings are not created or deleted here.
      </p>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load marketplace listings.
        </p>
      )}
      {update.isError && (
        <p className="screen-error" role="alert">
          {writeErrorMessage(update.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Listing</th>
            <th>Status</th>
            <th>Featured</th>
            <th>Category</th>
            <th>Installs</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {listings.map((l) => {
            const draft = categoryDraft[l.slug] ?? l.category ?? ''
            const nextCategory = draft.trim() === '' ? null : draft.trim()
            return (
              <tr key={l.id} className={l.status === 'deprecated' ? 'scr-row--muted' : undefined}>
                <td>
                  {name(l)}
                  <div className="scr-muted scr-mono">{l.slug}</div>
                </td>
                <td>
                  <span className={`scr-badge scr-badge--${l.status}`}>{l.status}</span>
                </td>
                <td>
                  <button
                    type="button"
                    className="screen-btn"
                    aria-label={`${l.featured ? 'Unfeature' : 'Feature'} ${name(l)}`}
                    disabled={update.isPending}
                    onClick={() => setPending({ listing: l, patch: { featured: !l.featured } })}
                  >
                    {l.featured ? 'Featured' : 'No'}
                  </button>
                </td>
                <td>
                  <span className="scr-actions">
                    <input
                      type="text"
                      className="scr-input scr-input--narrow"
                      aria-label={`Category for ${name(l)}`}
                      maxLength={64}
                      value={draft}
                      onChange={(e) =>
                        setCategoryDraft((d) => ({ ...d, [l.slug]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="screen-btn"
                      aria-label={`Save category for ${name(l)}`}
                      disabled={nextCategory === (l.category ?? null) || update.isPending}
                      onClick={() =>
                        setPending({ listing: l, patch: { category: nextCategory } })
                      }
                    >
                      Save
                    </button>
                  </span>
                </td>
                <td>{l.install_count ?? 0}</td>
                <td>
                  <span className="scr-actions">
                    {TRANSITIONS[l.status]?.map((t) => (
                      <button
                        key={t.to}
                        type="button"
                        className="screen-btn"
                        aria-label={`${t.label} ${name(l)}`}
                        disabled={update.isPending}
                        onClick={() => setPending({ listing: l, patch: { status: t.to } })}
                      >
                        {t.label}
                      </button>
                    ))}
                  </span>
                </td>
              </tr>
            )
          })}
          {!query.isLoading && listings.length === 0 && (
            <tr>
              <td colSpan={6} className="ws-empty">
                No marketplace listings.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Previous
        </button>
        <span className="ws-count">Page {Math.floor(offset / PAGE_SIZE) + 1}</span>
        <button type="button" disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
          Next
        </button>
      </div>

      {pending && (
        <ConfirmDialog
          label="Confirm listing change"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            setOkMsg(null)
            update.mutate(pending)
            setPending(null)
          }}
        >
          <p>
            <strong>{name(pending.listing)}</strong>: {describe(pending.listing, pending.patch)}?
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
